import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Our Chai', href: '/#about' },
  { label: 'Menu', href: '/menu' },
  { label: 'Green Tea Shop', href: '/shop' },
  { label: 'About', href: '/about' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('/#') && location.pathname === '/') {
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md shadow-sm'
      }`}
    >
      <nav className="flex items-center justify-between px-4 md:px-8 lg:px-12 h-16 md:h-20">
        <Link
          to="/"
          className="flex items-center gap-2"
          onClick={() => setMobileOpen(false)}
        >
          <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
            <span className="text-background-50 font-heading text-lg font-bold">G</span>
          </div>
          <span
            className={`font-heading text-xl md:text-2xl font-semibold tracking-wide ${
              transparent ? 'text-background-50' : 'text-foreground-900'
            }`}
          >
            Ghalib Chai
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.href.startsWith('/#') && location.pathname === '/' ? (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  transparent
                    ? 'text-background-50 hover:text-accent-400'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
                  transparent
                    ? 'text-background-50 hover:text-accent-400'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact"
            className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Order / Visit Us
          </Link>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center ${
            transparent ? 'text-background-50' : 'text-foreground-800'
          }`}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <i className="ri-close-line text-2xl" />
          ) : (
            <i className="ri-menu-line text-2xl" />
          )}
        </button>
      </nav>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/60">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.href.startsWith('/#') && location.pathname === '/' ? (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="block w-full text-left px-4 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block px-4 py-3 text-base font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-md transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="pt-3 border-t border-background-200/60">
              <Link
                to="/contact"
                className="block w-full text-center px-5 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Order / Visit Us
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}